import {
  parse,
  format,
  isBefore,
  isToday,
  addDays,
  addMinutes,
} from "date-fns";

import IndoorIcon from "@assets/icons/indoor.svg";
import BalconyIcon from "@assets/icons/balcony.svg";
import TerraceIcon from "@assets/icons/terrace.svg";
import PrivateRoomIcon from "@assets/icons/private-room.svg";

import { PiPicnicTableBold } from "react-icons/pi";

import { ka } from "date-fns/locale";
import { useTranslations } from "next-intl";

export const daysOfWeek = ["ორ", "სა", "ოთ", "ხუ", "პა", "შა", "კვ"];
export const daysOfWeekEng = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const workingDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const workingDaysFull = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const convertToMtavruli = (
  inputString: string | null | undefined,
): string => {
  if (typeof inputString !== "string") {
    console.warn(
      "convertToMtavruli received a non-string input. Defaulting to an empty string.",
    );
    inputString = ""; // Default to an empty string
  }

  let result = "";
  for (const char of inputString) {
    const codePoint = char.codePointAt(0);

    // Check if the character is a Georgian lowercase letter (Mkhedruli)
    if (codePoint !== undefined && codePoint >= 0x10d0 && codePoint <= 0x10f0) {
      // Convert to Mtavruli by adding the offset
      const mtavruliCodePoint = codePoint + 0x1c90 - 0x10d0;
      result += String.fromCodePoint(mtavruliCodePoint);
    } else {
      // Keep the character as is
      result += char;
    }
  }
  return result;
};

export interface ValidationErrorResponse {
  error?: {
    errors?: {
      [key: string]: {
        message: string;
      };
    };
  };
  status?: string;
  message?: string;
  string?: string;
}

export const handleErrors = (errorResponse: ValidationErrorResponse) => {
  // Handle the case with validation errors (with field-specific messages)
  if (errorResponse.status === "error") {
    const errorMessages: string[] = [];

    const errors = errorResponse.error?.errors;
    // Loop through the validation errors and collect all the messages
    for (const field in errors) {
      if (errors.hasOwnProperty(field)) {
        const errorMessage = errors[field].message;
        errorMessages.push(`${errorMessage}`);
      }
    }

    // Throw or log the combined validation error messages
    if (errorMessages.length > 0) {
      throw new Error(errorMessages.join("\n"));
    }
  }

  // Handle the case of a duplicate value or other general error messages
  if (errorResponse.status === "fail") {
    // If the error message contains "Duplicate field value", handle it differently
    if (errorResponse.message) {
      throw new Error(`${errorResponse.message}`);
    }
  }

  // Default error message if no specific structure matches
  throw new Error(`${errorResponse}`);
};

export const getFullDayName = (shortName: string): string => {
  const daysMap: { [key: string]: string } = {
    monday: "monday",
    tuesday: "tuesday",
    wednesday: "wednesday",
    thursday: "thursday",
    friday: "friday",
    saturday: "saturday",
    sunday: "sunday",
  };

  return daysMap[shortName] || shortName;
};

export const formatDate = (date: Date) => {
  return format(date, "dd MMM", { locale: ka });
};

export const getInfoCardIcons = (fontSize: number) => {
  const g = useTranslations("General");

  const indoorSpace = g("indoorSpace");
  const balcony = g("balcony");
  const terrace = g("terrace");
  const privateRoom = g("privateRoom");

  const iconsMap = {
    [indoorSpace]: <IndoorIcon />,
    [balcony]: <BalconyIcon />,
    [terrace]: <TerraceIcon />,
    [privateRoom]: <PrivateRoomIcon />,
    other: <PiPicnicTableBold />,
  };

  return iconsMap;
};

export const handleIncrement = (
  e: React.MouseEvent<HTMLButtonElement>,
  isCounter: boolean,
  count: number | undefined,
  setCount?: (count: number) => void,
) => {
  if (isCounter && setCount && count !== undefined) {
    setCount(count + 1);
  }
};

export const handleDecrement = (
  e: React.MouseEvent<HTMLButtonElement>,
  isCounter: boolean,
  count: number | undefined,
  setCount?: (count: number) => void,
) => {
  if (isCounter && setCount && count !== undefined) {
    setCount(count > 1 ? count - 1 : 1);
  }
};

export const handlePopupClick = (
  isDropdown: boolean,
  showPopup: boolean,
  setShowPopup?: (showPopup: boolean) => void,
) => {
  if (isDropdown && setShowPopup) {
    setShowPopup(!showPopup);
  }
};

export const togglePopup = (
  popupName: string,
  activePopup: string | null,
  setActivePopup: (popup: string | null) => void,
) => {
  setActivePopup(activePopup === popupName ? null : popupName);
};

export const toCamelCase = (str: string) => {
  return str
    .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))
    .replace(/^(.)/, (c) => c.toUpperCase());
};

// Convert the hex color to RGBA with 10% opacity
export const hexToRGBA = (hex: string, opacity: number) => {
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const generateTimeSlots = (
  from: string,
  to: string,
  interval: number,
  selectedDate: Date,
) => {
  // Parse 'from' and 'to' times as the selected date's times
  const fromDate = parse(from, "HH:mm", selectedDate);
  let toDate = parse(to, "HH:mm", selectedDate);

  // Adjust 'toDate' if it is equal to or earlier than 'fromDate'
  if (toDate <= fromDate) {
    // If 'toDate' is '00:00' or any time earlier than 'fromDate', add one day
    toDate = addDays(toDate, 1);
  }

  const isCurrentDay = isToday(selectedDate); // Check if the selected date is today

  const slots = [];
  let currentDate = fromDate;

  while (currentDate < toDate) {
    if (isCurrentDay) {
      // Only include slots that are in the future if it's today
      if (isBefore(new Date(), currentDate)) {
        slots.push(format(currentDate, "HH:mm"));
      }
    } else {
      // Include all slots if it's not today
      slots.push(format(currentDate, "HH:mm"));
    }

    // Increment currentDate by the interval
    currentDate = addMinutes(currentDate, interval);
  }
  return slots;
};
